import { Loader } from '@percent/cause-dashboard/common/components'
import styles from './UsersListTable.module.scss'
import {
  Badge,
  Menu,
  SectionProps,
  Table,
  TableCell,
  TablePagination,
  TableProvider,
  TableRow
} from '@percent/lemonade'
import { IamGetAccounts } from '@percent/cause-dashboard/services/types/iamResponses.types'
import { EditUserRoleModal } from './EditUserRoleModal/EditUserRoleModal'
import { useAuthState, usePermissionCheck, useQuery, useTranslatedRole } from '@percent/cause-dashboard/common/hooks'
import { useTranslation } from 'react-i18next'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import React, { useState } from 'react'
import { RemoveUserModal } from './RemoveUserModal/RemoveUserModal'

interface UsersListTableProps {
  isLoading: boolean
  data: IamGetAccounts[] | null
  totalResultCount: number
  onNextPageClick: (() => void) | null
  onPreviousPageClick: (() => void) | null
  onRefetchData: () => void
}

export function UsersListTable({
  data,
  isLoading,
  totalResultCount,
  onNextPageClick: nextPage,
  onPreviousPageClick: previousPage,
  onRefetchData
}: Readonly<UsersListTableProps>) {
  const { t } = useTranslation()
  const { translateRole } = useTranslatedRole()
  const [currentlUserIdToEdit, setCurrentlUserIdToEdit] = useState<string>('')
  const [currentUserIdToRemove, setCurrentUserIdToRemove] = useState<string>('')

  const { iamService } = useServices()
  const [{ data: rolesData }] = useQuery(iamService.getRoles, {})
  const allRoles = rolesData?.data?.data?.roles

  const {
    authState: { user: currentUser }
  } = useAuthState()
  const { userCan } = usePermissionCheck()

  const canPerformActions = userCan('organization_users:write')

  if (isLoading) {
    return <Loader />
  }

  const handleRoleChanged = (refetch: boolean) => {
    setCurrentlUserIdToEdit('')
    if (refetch) {
      onRefetchData()
    }
  }

  const handleUserDeleted = (refetch: boolean) => {
    setCurrentUserIdToRemove('')
    if (refetch) {
      onRefetchData()
    }
  }

  const columns = [
    { id: t('typography.userManagement.email'), isSortable: false, props: { width: '80%' } },
    { id: t('typography.userManagement.role'), isSortable: false, props: { width: '20%' } },
    ...(canPerformActions
      ? [{ id: t('typography.userManagement.actions'), isSortable: false, props: { width: 'auto' } }]
      : [])
  ]

  const getMenuSections = (id: string): SectionProps[] => [
    {
      items: [
        {
          key: 'edit',
          label: t('typography.userManagement.edit'),
          shouldBeDisabled: false,
          onClick: () => setCurrentlUserIdToEdit(id)
        },
        {
          key: 'remove',
          label: t('typography.userManagement.remove'),
          shouldBeDisabled: false,
          onClick: () => setCurrentUserIdToRemove(id)
        }
      ]
    }
  ]

  return (
    <TableProvider
      isLoading={isLoading}
      totalResults={totalResultCount || 0}
      columns={columns}
      emptyTableTitle={t('table.noUsers')}
      illustration="no-results"
    >
      <Table>
        <div data-testid={'users-table'} />
        {data?.map(({ id, email, role }) => (
          <React.Fragment key={`permissions-list-${id}`}>
            <EditUserRoleModal
              accountId={id}
              email={email}
              role={role}
              open={currentlUserIdToEdit === id}
              close={handleRoleChanged}
              roles={allRoles ?? []}
            />
            <RemoveUserModal
              accountId={id}
              email={email}
              open={currentUserIdToRemove === id}
              close={handleUserDeleted}
            />
            <TableRow key={`permissions-list-${id}`} data-testid={`row-${id}`}>
              <TableCell>
                <div className={styles.cellWrapper}>
                  <span>{email}</span>
                </div>
              </TableCell>
              <TableCell dataTestId={`row-${id}-role-${role?.displayName ?? 'none'}`}>
                {role ? (
                  <Badge variant="default" testId="role-badge">
                    {translateRole(role).displayName}
                  </Badge>
                ) : (
                  <span data-testid="role-empty">&mdash;</span>
                )}
              </TableCell>
              {currentUser?.id !== id && canPerformActions && (
                <TableCell dataTestId={`row-${id}-action`}>
                  <Menu title={t('typography.userManagement.more')} sections={getMenuSections(id)} />
                </TableCell>
              )}
            </TableRow>
          </React.Fragment>
        ))}
      </Table>
      <TablePagination
        previousPage={previousPage}
        nextPage={nextPage}
        totalResultsText={t('typography.results', { count: totalResultCount })}
      />
    </TableProvider>
  )
}
